import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Divider,
  Stack
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import StarIcon from "@mui/icons-material/Star";

const PrinterManagement = () => {
  const [printers, setPrinters] = useState([]);
  const [savedPrinter, setSavedPrinter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testingPrinter, setTestingPrinter] = useState(null);
  const [selectedPrinter, setSelectedPrinter] = useState(null);

  useEffect(() => {
    loadSavedPrinter();
    fetchPrinters();
  }, []);

  const loadSavedPrinter = () => {
    if (!window.AndroidPOS?.getSavedPrinter) return;

    const callbackId = `saved_printer_${Date.now()}`;
    window[callbackId] = (response) => {
      try {
        const data = typeof response === "string" ? JSON.parse(response) : response;
        if (data.success && data.printer) {
          setSavedPrinter(data.printer);
        }
      } catch (err) {
        console.error("Failed to parse saved printer", err);
      } finally {
        delete window[callbackId];
      }
    };

    window.AndroidPOS.getSavedPrinter(callbackId);
  };

  const fetchPrinters = () => {
    if (!window.AndroidPOS?.getAvailablePrinters) {
      setError("Android printer interface not available");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const callbackId = `printers_${Date.now()}`;
    window[callbackId] = (response) => {
      try {
        const data = typeof response === "string" ? JSON.parse(response) : response;
        
        if (data.success) {
          setPrinters(data.printers || []);
          if (data.printers.length === 0) {
            setError("No printers detected. Please check USB connection.");
          }
        } else {
          setError(data.message || "Failed to get printers");
        }
      } catch (err) {
        console.error("Failed to parse printer data", err);
        setError("Failed to load printer list");
      } finally {
        setLoading(false);
        delete window[callbackId];
      }
    };

    window.AndroidPOS.getAvailablePrinters(callbackId);
  };

  const handleRescan = () => {
    if (!window.AndroidPOS?.rescanPrinters) {
      fetchPrinters();
      return;
    }

    setLoading(true);
    setError(null);

    const callbackId = `rescan_${Date.now()}`;
    window[callbackId] = (response) => {
      try {
        const data = typeof response === "string" ? JSON.parse(response) : response;
        
        if (data.success) {
          setPrinters(data.printers || []);
          if (data.printers.length === 0) {
            setError("No printers found after rescan");
          }
        } else {
          setError(data.message || "Rescan failed");
        }
      } catch (err) {
        setError("Failed to rescan printers");
      } finally {
        setLoading(false);
        delete window[callbackId];
      }
    };

    window.AndroidPOS.rescanPrinters(callbackId);
  };

  const handleSelectPrinter = (printer) => {
    if (!window.AndroidPOS?.selectPrinter) {
      alert("Printer selection not supported");
      return;
    }

    setSelectedPrinter(printer.id);

    const callbackId = `select_${Date.now()}`;
    window[callbackId] = (response) => {
      try {
        const data = typeof response === "string" ? JSON.parse(response) : response;
        
        if (data.success) {
          setSavedPrinter(printer);
          alert(`✓ ${printer.name} selected and connected successfully!`);
        } else {
          alert(`Failed to select printer: ${data.message}`);
        }
      } catch (err) {
        alert("Failed to select printer");
      } finally {
        setSelectedPrinter(null);
        delete window[callbackId];
      }
    };

    const printerData = JSON.stringify({
      id: printer.id,
      name: printer.name,
      vendorId: printer.vendorId,
      productId: printer.productId
    });

    window.AndroidPOS.selectPrinter(printerData, callbackId);
  };

  const handleTestPrint = (printer) => {
    if (!window.AndroidPOS?.testPrint) {
      alert("Test print not supported");
      return;
    }

    setTestingPrinter(printer.id);

    const callbackId = `test_${Date.now()}`;
    window[callbackId] = (response) => {
      try {
        const data = typeof response === "string" ? JSON.parse(response) : response;
        
        if (data.success) {
          alert(`✓ Test print successful on ${printer.name}`);
        } else {
          alert(`Test print failed: ${data.message}`);
        }
      } catch (err) {
        alert("Test print failed");
      } finally {
        setTestingPrinter(null);
        delete window[callbackId];
      }
    };

    window.AndroidPOS.testPrint(printer.id, callbackId);
  };

  const handleClearSaved = () => {
    if (!window.AndroidPOS?.clearSavedPrinter) {
      alert("Clear printer not supported");
      return;
    }

    const callbackId = `clear_${Date.now()}`;
    window[callbackId] = (response) => {
      try {
        const data = typeof response === "string" ? JSON.parse(response) : response;
        
        if (data.success) {
          setSavedPrinter(null);
          alert("Printer selection cleared");
        }
      } catch (err) {
        alert("Failed to clear printer");
      } finally {
        delete window[callbackId];
      }
    };

    window.AndroidPOS.clearSavedPrinter(callbackId);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={48} />
          <Typography color="text.secondary">Scanning for printers...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Printer Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRescan}
          disabled={loading}
        >
          Rescan
        </Button>
      </Box>

      {savedPrinter && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleClearSaved}>
              Clear
            </Button>
          }
        >
          <strong>Active Printer:</strong> {savedPrinter.name}
        </Alert>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {printers.length === 0 && !error && (
        <Alert severity="info">
          No printers detected. Please ensure your printer is connected via USB and try rescanning.
        </Alert>
      )}

      <Stack spacing={2}>
        {printers.map((printer) => {
          const isActive = savedPrinter?.id === printer.id;
          const isSelecting = selectedPrinter === printer.id;
          const isTesting = testingPrinter === printer.id;

          return (
            <Card 
              key={printer.id} 
              variant="outlined"
              sx={{ 
                borderColor: isActive ? "success.main" : "divider",
                borderWidth: isActive ? 2 : 1
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                    <PrintIcon color={isActive ? "success" : "action"} />
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontWeight: 500 }}>
                          {printer.name}
                        </Typography>
                        {isActive && (
                          <CheckCircleIcon sx={{ fontSize: 20, color: "success.main" }} />
                        )}
                        {printer.isRecommended && (
                          <Chip 
                            label="Recommended" 
                            size="small" 
                            color="primary"
                            icon={<StarIcon />}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        VID: 0x{printer.vendorId.toString(16).toUpperCase()} • 
                        PID: 0x{printer.productId.toString(16).toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant={isActive ? "outlined" : "contained"}
                      size="small"
                      onClick={() => handleSelectPrinter(printer)}
                      disabled={isActive || isSelecting || isTesting}
                    >
                      {isSelecting ? (
                        <CircularProgress size={20} />
                      ) : isActive ? (
                        "Active"
                      ) : (
                        "Select"
                      )}
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleTestPrint(printer)}
                      disabled={isTesting || isSelecting}
                    >
                      {isTesting ? <CircularProgress size={20} /> : "Test Print"}
                    </Button>
                  </Box>
                </Box>

                {isActive && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="success.main">
                      This printer will be used for all print jobs
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          <strong>Troubleshooting Tips:</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          • If no printers appear, unplug and reconnect the USB cable
          <br />
          • Try clicking "Rescan" after reconnecting
          <br />
          • Make sure the printer is powered on
          <br />
          • If a printer stops working, select it again from this list
          <br />
          • Use "Test Print" to verify printer connectivity
        </Typography>
      </Box>
    </Box>
  );
};

export default PrinterManagement;